import type {
  InitiativeBusinessValueSummary,
  InitiativeCreateData,
  UseCaseData,
} from "@/contexts/UseCaseContext";

const BASE = "/api/v1/initiatives";

export type DecisionBriefField =
  | "problem_statement"
  | "desired_outcome"
  | "success_criterion"
  | "scope"
  | "non_goals"
  | "constraints"
  | "dependencies"
  | "rough_effort"
  | "risks";

export interface InitiativeDecisionBrief {
  initiative_id: string;
  owner_user_id: string | null;
  problem_statement: string | null;
  desired_outcome: string | null;
  success_criterion: string | null;
  scope: string | null;
  non_goals: string | null;
  constraints: string | null;
  dependencies: string | null;
  rough_effort: string | null;
  risks: string | null;
  approver_user_id: string | null;
  ready: boolean;
  missing_fields: string[];
  updated_at: string | null;
}

export type InitiativeDecisionBriefInput = Partial<
  Pick<InitiativeDecisionBrief, DecisionBriefField | "approver_user_id">
>;

export interface DecisionBriefApprover {
  user_id: string;
  name: string;
  email: string;
}

export type InitiativeApprovalStatus =
  | "draft"
  | "submitted"
  | "changes_requested"
  | "approved"
  | "rejected";

export interface InitiativeApprovalEvent {
  id: string;
  sequence: number;
  event_type: InitiativeApprovalStatus | "submitted";
  actor_user_id: string;
  reason: string | null;
  questions: string | null;
  created_at: string;
}

export interface InitiativeApproval {
  initiative_id: string;
  status: InitiativeApprovalStatus;
  owner_user_id: string | null;
  approver_user_id: string | null;
  ready: boolean;
  missing_fields: string[];
  can_submit: boolean;
  can_decide: boolean;
  submitted_by_user_id: string | null;
  submitted_at: string | null;
  decided_by_user_id: string | null;
  decided_at: string | null;
  reason: string | null;
  questions: string | null;
  events: InitiativeApprovalEvent[];
}

export interface InitiativeApprovalDecisionInput {
  decision: "approve" | "reject" | "request_changes";
  reason?: string | null;
  questions?: string | null;
}

function mapBusinessValue(
  value: Record<string, unknown> | null | undefined,
): InitiativeBusinessValueSummary | undefined {
  if (!value) return undefined;
  return {
    state: value.state as InitiativeBusinessValueSummary["state"],
    sourceStatus: value.source_status as InitiativeBusinessValueSummary["sourceStatus"],
    expectedOutcome: value.expected_outcome as string | undefined,
    kpiName: value.kpi_name as string | undefined,
    baselineValue: value.baseline_value as number | undefined,
    targetValue: value.target_value as number | undefined,
    unit: value.unit as string | undefined,
    targetDate: value.target_date as string | undefined,
    expectedFinancialValue: value.expected_financial_value as number | undefined,
    forecastCost: value.forecast_cost as number | undefined,
    currency: value.currency as string | undefined,
    benefitOwnerUserId: value.benefit_owner_user_id as string | undefined,
    confidence: value.confidence as InitiativeBusinessValueSummary["confidence"],
    nextReviewDate: value.next_review_date as string | undefined,
    latestActualValue: value.latest_actual_value as number | undefined,
    latestMeasurementDate: value.latest_measurement_date as string | undefined,
    progress: value.progress as number | undefined,
    reviewOverdue: Boolean(value.review_overdue),
    changeReviewRequired: Boolean(value.change_review_required),
    missingFields: (value.missing_fields as string[] | undefined) ?? [],
    materialChanges: (value.material_changes as string[] | undefined) ?? [],
  };
}

// The API uses snake_case for some fields; map back to camelCase for the frontend
function mapResponse(data: Record<string, unknown>): UseCaseData {
  return {
    id: data.id as string,
    name: data.name as string,
    // ADR-0014: API now sends org_unit ids + an org_unit_names {id: name} map.
    // Keep the camelCase field names but carry ids; normalize approval records
    // (keyed org_unit_id) onto the existing ``org_unit`` field.
    org_unit: (data.org_unit_id as string) ?? "",
    org_units: (data.org_unit_ids as string[] | undefined) ?? [],
    orgUnitApprovals: ((data.org_unit_approvals as Array<Record<string, unknown>> | undefined) ?? []).map(
      (r) => ({ ...r, org_unit: (r.org_unit_id as string) ?? (r.org_unit as string) })
    ) as UseCaseData["orgUnitApprovals"],
    orgUnitNames: (data.org_unit_names as Record<string, string> | undefined) ?? {},
    nominationStatus: data.nomination_status as UseCaseData["nominationStatus"],
    stage: data.stage as UseCaseData["stage"],
    impact: data.impact as UseCaseData["impact"],
    risk: data.risk as UseCaseData["risk"],
    readiness: data.readiness as number,
    owner: data.owner as string,
    ownerUserId: data.owner_user_id as string | undefined,
    description: (data.description as string) ?? "",
    team: (data.team as string[]) ?? [],
    readers: (data.readers as string[]) ?? [],
    vendors: (data.vendors as string[] | undefined) ?? [],
    raci: (data.raci as UseCaseData["raci"]) ?? [],
    budget: (data.budget as string) ?? "",
    startDate: (data.start_date as string) ?? "",
    endDate: (data.end_date as string) ?? "",
    ragRating: data.rag_rating as UseCaseData["ragRating"],
    risks: (data.risks as UseCaseData["risks"]) ?? [],
    initiativeType: data.initiative_type as UseCaseData["initiativeType"],
    archived: (data.archived as boolean) ?? false,
    vendorBriefing: data.vendor_briefing as UseCaseData["vendorBriefing"],
    vendorBids: (data.vendor_bids as UseCaseData["vendorBids"]) ?? [],
    adoptionConfirmation: data.adoption_confirmation as UseCaseData["adoptionConfirmation"],
    adoptionEvidence: (data.adoption_evidence as UseCaseData["adoptionEvidence"]) ?? [],
    adoptionDirection: data.adoption_direction as UseCaseData["adoptionDirection"],
    businessValue: mapBusinessValue(
      data.business_value as Record<string, unknown> | null | undefined,
    ),
    comments: (data.comments as UseCaseData["comments"]) ?? [],
    finalApproval: data.final_approval as UseCaseData["finalApproval"],
    pendingChanges: (data.pending_changes as boolean) ?? false,
    lastChangeBy: data.last_change_by as string | undefined,
    lastChangeAt: data.last_change_at as string | undefined,
  };
}

// Map camelCase frontend fields to snake_case API fields
function mapRequest(
  data: Partial<UseCaseData> & Pick<InitiativeCreateData, "teamUserIds" | "readerUserIds">
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const pass = (from: keyof typeof data, to: string) => {
    if (from in data) out[to] = data[from];
  };
  pass("name", "name");
  // ADR-0014: send org_unit ids under the renamed API keys.
  if ("org_unit" in data) out["org_unit_id"] = data.org_unit || null;
  if ("org_units" in data) out["org_unit_ids"] = data.org_units;
  pass("stage", "stage");
  pass("impact", "impact");
  pass("risk", "risk");
  pass("readiness", "readiness");
  pass("owner", "owner");
  if ("ownerUserId" in data) out["owner_user_id"] = data.ownerUserId;
  pass("description", "description");
  pass("team", "team");
  pass("readers", "readers");
  if ("teamUserIds" in data) out["team_user_ids"] = data.teamUserIds;
  if ("readerUserIds" in data) out["reader_user_ids"] = data.readerUserIds;
  pass("vendors", "vendors");
  pass("raci", "raci");
  pass("budget", "budget");
  if ("startDate" in data) out["start_date"] = data.startDate;
  if ("endDate" in data) out["end_date"] = data.endDate;
  if ("ragRating" in data) out["rag_rating"] = data.ragRating;
  pass("risks", "risks");
  if ("initiativeType" in data) out["initiative_type"] = data.initiativeType;
  pass("archived", "archived");
  if ("vendorBriefing" in data) out["vendor_briefing"] = data.vendorBriefing;
  if ("vendorBids" in data) out["vendor_bids"] = data.vendorBids;
  if ("adoptionConfirmation" in data) out["adoption_confirmation"] = data.adoptionConfirmation;
  if ("adoptionEvidence" in data) out["adoption_evidence"] = data.adoptionEvidence;
  if ("adoptionDirection" in data) out["adoption_direction"] = data.adoptionDirection;
  pass("comments", "comments");
  if ("pendingChanges" in data) out["pending_changes"] = data.pendingChanges;
  if ("lastChangeBy" in data) out["last_change_by"] = data.lastChangeBy;
  if ("lastChangeAt" in data) out["last_change_at"] = data.lastChangeAt;
  return out;
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(errorMessage(err, res.statusText));
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const initiativeService = {
  list: (archived = false): Promise<UseCaseData[]> =>
    request<{ items: Record<string, unknown>[]; total: number; page: number; limit: number }>(
      `${BASE}?archived=${archived}`
    ).then((res) => res.items.map(mapResponse)),

  get: (id: string): Promise<UseCaseData> =>
    request<Record<string, unknown>>(`${BASE}/${id}`).then(mapResponse),

  create: (data: InitiativeCreateData): Promise<UseCaseData> =>
    request<Record<string, unknown>>(BASE, {
      method: "POST",
      body: JSON.stringify(mapRequest(data)),
    }).then(mapResponse),

  update: (id: string, data: Partial<UseCaseData>): Promise<UseCaseData> =>
    request<Record<string, unknown>>(`${BASE}/${id}`, {
      method: "PUT",
      body: JSON.stringify(mapRequest(data)),
    }).then(mapResponse),

  archive: (id: string): Promise<UseCaseData> =>
    request<Record<string, unknown>>(`${BASE}/${id}/archive`, { method: "PATCH" }).then(
      mapResponse
    ),

  delete: (id: string): Promise<void> =>
    request<void>(`${BASE}/${id}`, { method: "DELETE" }),

  getDecisionBrief: (id: string): Promise<InitiativeDecisionBrief> =>
    request(`${BASE}/${id}/decision-brief`),

  saveDecisionBrief: (
    id: string,
    data: InitiativeDecisionBriefInput,
  ): Promise<InitiativeDecisionBrief> =>
    request(`${BASE}/${id}/decision-brief`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  getDecisionBriefApprovers: (id: string): Promise<DecisionBriefApprover[]> =>
    request(`${BASE}/${id}/decision-brief/approvers`),

  getApproval: (id: string): Promise<InitiativeApproval> =>
    request(`${BASE}/${id}/approval`),

  submitApproval: (id: string): Promise<InitiativeApproval> =>
    request(`${BASE}/${id}/approval/submit`, { method: "POST" }),

  decideApproval: (
    id: string,
    data: InitiativeApprovalDecisionInput,
  ): Promise<InitiativeApproval> =>
    request(`${BASE}/${id}/approval/decision`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // ── Workflow ──────────────────────────────────────────────────────────────

  submitForApproval: (id: string): Promise<{ ok: boolean; nomination_status: string }> =>
    request(`${BASE}/${id}/workflow/submit`, { method: "POST" }),

  approveOrgUnit: (
    id: string,
    // ``orgUnitId`` is an org_unit id (ADR-0014); sent to the API as org_unit_id.
    body: { orgUnitId: string; status: string; comment?: string }
  ): Promise<{ ok: boolean; nomination_status: string }> =>
    request(`${BASE}/${id}/workflow/approve-org-unit`, {
      method: "POST",
      body: JSON.stringify({
        org_unit_id: body.orgUnitId,
        status: body.status,
        comment: body.comment,
      }),
    }),

  finalApproval: (
    id: string,
    body: { status: string; comment?: string }
  ): Promise<{ ok: boolean; nomination_status: string }> =>
    request(`${BASE}/${id}/workflow/final-approval`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  respondToNomination: (
    id: string,
    body: { action: string; reason?: string }
  ): Promise<{ ok: boolean; status: string }> =>
    request(`${BASE}/${id}/workflow/respond`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── Participants ──────────────────────────────────────────────────────────

  getParticipants: (id: string): Promise<Participant[]> =>
    request(`${BASE}/${id}/participants`),

  addParticipants: (
    id: string,
    body: { user_ids: string[]; role: string }
  ): Promise<{ ok: boolean; added: number }> =>
    request(`${BASE}/${id}/participants`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  removeParticipant: (id: string, userId: string): Promise<void> =>
    request(`${BASE}/${id}/participants/${userId}`, { method: "DELETE" }),

  // ── OrgUnit Heads ────────────────────────────────────────────────────

  getOrgUnitHeads: (id: string): Promise<Record<string, { user_id: string; name: string }[]>> =>
    request(`${BASE}/${id}/workflow/org-unit-heads`),

  getWorkflowPermissions: (id: string): Promise<{
    approvableOrgUnitIds: string[];
    canFinalApprove: boolean;
  }> =>
    request<{ approvable_org_unit_ids: string[]; can_final_approve: boolean }>(
      `${BASE}/${id}/workflow/permissions`,
    ).then((data) => ({
      approvableOrgUnitIds: data.approvable_org_unit_ids,
      canFinalApprove: data.can_final_approve,
    })),

  // ── Ideas ────────────────────────────────────────────────────────────────
  getInitiativeIdeas: (id: string): Promise<LinkedIdea[]> =>
    request(`${BASE}/${id}/ideas`),

  linkIdeasToInitiative: (id: string, ideaIds: string[]): Promise<{ linked: number; skipped: number }> =>
    request(`${BASE}/${id}/ideas`, {
      method: "POST",
      body: JSON.stringify({ idea_ids: ideaIds }),
    }),

  unlinkIdeaFromInitiative: (id: string, ideaId: string): Promise<void> =>
    request(`${BASE}/${id}/ideas/${ideaId}`, { method: "DELETE" }),

  // ── Initiative Value Management ──
  getValueCase: (id: string): Promise<InitiativeValueCase> =>
    request(`${BASE}/${id}/value-case`),

  saveValueCaseDraft: (
    id: string,
    data: ValueCaseDraftInput
  ): Promise<InitiativeValueCase> =>
    request(`${BASE}/${id}/value-case/draft`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  confirmValueCaseDraft: (
    id: string,
    expectedLockVersion: number
  ): Promise<InitiativeValueCase> =>
    request(`${BASE}/${id}/value-case/draft/confirm`, {
      method: "POST",
      body: JSON.stringify({ expected_lock_version: expectedLockVersion }),
    }),

  discardValueCaseDraft: (
    id: string,
    expectedLockVersion: number
  ): Promise<InitiativeValueCase> =>
    request(`${BASE}/${id}/value-case/draft/discard`, {
      method: "POST",
      body: JSON.stringify({ expected_lock_version: expectedLockVersion }),
    }),

  reviewValueCaseSuggestion: (
    id: string,
    suggestionId: string,
    decision: "accept" | "reject",
    expectedLockVersion: number
  ): Promise<InitiativeValueCase> =>
    request(
      `${BASE}/${id}/value-case/suggestions/${suggestionId}/${decision}`,
      {
        method: "POST",
        body: JSON.stringify({ expected_lock_version: expectedLockVersion }),
      }
    ),

  getValueCaseHistory: (id: string): Promise<ValueCaseVersion[]> =>
    request(`${BASE}/${id}/value-case/history`),

  getValueUpdates: (id: string): Promise<InitiativeValueUpdate[]> =>
    request(`${BASE}/${id}/value-updates`),

  recordValueUpdate: (
    id: string,
    data: InitiativeValueUpdateInput
  ): Promise<InitiativeValueUpdate> =>
    request(`${BASE}/${id}/value-updates`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

export type ValueCaseState =
  | "missing"
  | "draft"
  | "needs_confirmation"
  | "complete"
  | "review_overdue";

export type ValueType =
  | "cost_reduction"
  | "revenue_growth"
  | "capacity_or_time_saved"
  | "risk_reduction"
  | "service_quality"
  | "employee_experience"
  | "other";

export type MeasurementKind =
  | "number"
  | "percentage"
  | "currency"
  | "duration"
  | "count";

export interface ValueCaseVersion {
  id: string;
  initiative_id: string;
  version: number;
  status: "draft" | "confirmed" | "superseded";
  lock_version: number;
  value_type: ValueType | null;
  expected_outcome: string | null;
  kpi_name: string | null;
  measurement_kind: MeasurementKind | null;
  baseline_value: string | null;
  target_value: string | null;
  target_direction: "increase" | "decrease" | "maintain" | null;
  unit: string | null;
  target_date: string | null;
  measurement_source: string | null;
  expected_financial_value: string | null;
  financial_value_period: "annual" | "total" | null;
  forecast_cost: string | null;
  currency: string | null;
  benefit_owner_user_id: string | null;
  confidence: "low" | "medium" | "high" | null;
  next_review_date: string | null;
  confirmed_by_user_id: string | null;
  confirmed_at: string | null;
  created_by_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

function errorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== "object" || !("detail" in payload)) {
    return fallback;
  }
  const detail = (payload as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (!detail || typeof detail !== "object") return fallback;

  const structured = detail as {
    message?: unknown;
    issues?: Array<{ message?: unknown }>;
  };
  if (typeof structured.message === "string") return structured.message;
  const issueMessages = (structured.issues ?? [])
    .map((issue) => issue.message)
    .filter((message): message is string => typeof message === "string");
  return issueMessages.length > 0 ? issueMessages.join("; ") : fallback;
}

export interface ValueCaseSuggestion {
  id: string;
  field_name: string;
  suggested_value: unknown;
  source_type: "idea_explicit" | "idea_inferred" | "ai_generated" | "imported";
  source_resource_id: string | null;
  confidence: "low" | "medium" | "high" | null;
  rationale: string | null;
  status: "pending" | "accepted" | "rejected";
  created_at: string | null;
}

export interface InitiativeValueUpdate {
  id: string;
  initiative_id: string;
  value_case_id: string;
  measurement_date: string;
  actual_value: string;
  unit_snapshot: string;
  evidence: unknown;
  commentary: string | null;
  source_type: "user_reported" | "imported" | "system_integration";
  created_by_user_id: string | null;
  supersedes_update_id: string | null;
  created_at: string | null;
}

export interface InitiativeValueCase {
  state: ValueCaseState;
  missing_fields: string[];
  review_overdue: boolean;
  change_review_required: boolean;
  material_changes: string[];
  confirmed: ValueCaseVersion | null;
  draft: ValueCaseVersion | null;
  pending_suggestions: ValueCaseSuggestion[];
  latest_update: InitiativeValueUpdate | null;
  progress: string | null;
}

export type ValueCaseDraftInput = Partial<
  Pick<
    ValueCaseVersion,
    | "value_type"
    | "expected_outcome"
    | "kpi_name"
    | "measurement_kind"
    | "baseline_value"
    | "target_value"
    | "target_direction"
    | "unit"
    | "target_date"
    | "measurement_source"
    | "expected_financial_value"
    | "financial_value_period"
    | "forecast_cost"
    | "currency"
    | "benefit_owner_user_id"
    | "confidence"
    | "next_review_date"
  >
> & { expected_lock_version: number };

export interface InitiativeValueUpdateInput {
  measurement_date: string;
  actual_value: string;
  evidence?: unknown;
  commentary?: string | null;
  supersedes_update_id?: string | null;
}

export interface LinkedIdea {
  id: string;
  idea_text: string;
  status: string;
  submitter_name: string | null;
  submitter_org_unit: string | null;
  urgency: string | null;
  created_at: string | null;
  linked_at: string | null;
}

export interface Participant {
  user_id: string;
  role: string;
  status: string;
  decline_reason: string | null;
  invited_at: string | null;
  responded_at: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string;
}
